import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  GraduationCap,
  Shield,
  Star,
  Target,
  Trophy,
  Zap
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import axios from 'axios';
import ReviewsList from '@/components/reviews/ReviewsList';
import { ReviewType } from '@/types/review.types';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_CERTIFICATIONS_PERMISSION } from '@/constants/constant-permissions';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { getExamQuestionCount, getExamTotalPointsLabel } from '@/utils/examQuestionCount';
import TierThresholds from '@/components/certifications/TierThresholds';

interface CertificationExamDetails {
  _id: string;
  certification_id: string;
  title: string;
  description?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  questions: any[];
  total_points: number;
  passing_score: number;
  time_limit_minutes?: number;
  max_attempts?: number;
  tier_config?: {
    enabled: boolean;
    gold_threshold: number;
    silver_threshold: number;
    bronze_threshold: number;
  };
  shuffle_questions: boolean;
  shuffle_options: boolean;
  is_published: boolean;
  generated_by: string;
  createdAt: Date;
  updatedAt: Date;
}

interface CertificationPreviewProps {
  isLearner?: boolean;
}

export default function CertificationPreview({ isLearner = false }: CertificationPreviewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Permission guards
  const hasWritePermission = useHasPermission([LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE]);
  const canWrite = !isLearner || hasWritePermission;

  // Redirect learners without any permission to access-denied
  // Read sessionStorage directly to avoid race condition with useHasPermission's useState(false) initial value
  useEffect(() => {
    if (!isLearner) return;
    const permissions: string[] = JSON.parse(sessionStorage.getItem('permissions') || '[]');
    const hasPermission = permissions.some(p =>
      p === LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_READ ||
      p === LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE
    );
    if (!hasPermission) {
      navigate('/access-denied', { replace: true });
    }
  }, [isLearner, navigate]);

  // Get initial tab from URL query param or default to 'overview'
  const initialTab = searchParams.get('tab') || 'overview';
  const shouldOpenReviewModal = searchParams.get('action') === 'write';

  const [certificationData, setCertificationData] = useState<CertificationExamDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [, setUserAttempts] = useState<any[]>([]);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [accessVerified, setAccessVerified] = useState(!isLearner); // Skip verification for admin
  const [accessDenied, setAccessDenied] = useState(false);
  const accessCheckDone = useRef(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'exam-info', label: 'Exam Info', icon: AlertCircle },
    { id: 'requirements', label: 'Requirements', icon: Target },
    { id: 'reviews', label: 'Reviews', icon: Star }
  ];

  // Verify resource access before loading certification details (only for learners)
  const verifyAccess = useCallback(async () => {
    if (!id || !isLearner || accessCheckDone.current) return;

    accessCheckDone.current = true;

    try {
      await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_RESOURCE_ACCESS, {
        certification_id: id
      });
      setAccessVerified(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data?.error;
        if (errorCode === 'ACCESS_004' || error.response?.status === 403) {
          setAccessDenied(true);
          return;
        }
      }
      // For other errors, allow access and let the page handle it
      setAccessVerified(true);
    }
  }, [id, isLearner]);

  const loadCertificationDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.GET_DETAILS(id));

      if (response.data.success) {
        setCertificationData(response.data.data);

        // Fetch user attempts to check if all attempts are used
        try {
          const attemptsResponse = await apiClient.get(
            API_ENDPOINTS.CERTIFICATIONS.LIST_ATTEMPTS(id)
          );

          if (attemptsResponse.data.success && attemptsResponse.data.data) {
            const attempts = attemptsResponse.data.data;
            setUserAttempts(attempts);

            // Count completed and abandoned attempts (these count as "used")
            const usedAttempts = attempts.filter(
              (a: any) => a.status === 'COMPLETED' || a.status === 'SUBMITTED' || a.status === 'ABANDONED'
            ).length;
            setAttemptsUsed(usedAttempts);
          }
        } catch (attemptsError) {
          console.error('Error loading attempts:', attemptsError);
          // Don't show error toast, just log it
        }
      }
    } catch (error) {
      console.error('Error loading certification:', error);
      toast.error('Failed to load certification details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // First verify access when component mounts (only for learners)
  useEffect(() => {
    if (isLearner) {
      verifyAccess();
    }
  }, [isLearner, verifyAccess]);

  // Disable body scroll when access is denied
  useEffect(() => {
    if (accessDenied) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [accessDenied]);

  // Load certification details only after access is verified
  useEffect(() => {
    if (accessVerified) {
      loadCertificationDetails();
    }
  }, [accessVerified, loadCertificationDetails]);

  const handleStartExam = () => {
    navigate(`/learn/exams/start/${id}`);
  };

  const handleGoBack = () => {
    // Navigate back to admin or learner certifications based on isLearner prop
    navigate(isLearner ? '/learn/certifications' : '/admin/certifications');
  };

  // Show skeleton while verifying access (only for learners)
  if (isLearner && !accessVerified && !accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SkeletonLoader type="custom" className="w-full h-40 sm:h-56 md:h-80 lg:h-96 rounded-none" />
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8">
              <div className="flex-1 space-y-4">
                <SkeletonLoader type="custom" className="h-4 w-40 rounded" />
                <SkeletonLoader type="custom" className="h-8 w-3/4 rounded" />
                <div className="flex gap-4">
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-28 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                </div>
              </div>
              <SkeletonLoader type="button" className="h-12 w-36" />
            </div>
          </div>
        </div>
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex gap-6 py-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonLoader key={i} type="custom" className="h-5 w-20 rounded" />
              ))}
            </div>
          </div>
        </div>
        <div className="container mx-auto px-0 sm:px-4 py-4 sm:py-6 md:py-8">
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 order-1 min-w-0">
              <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-4">
                <SkeletonLoader type="title" />
                <SkeletonLoader type="text" count={4} />
                <SkeletonLoader type="custom" className="h-24 w-full rounded-lg mt-4" />
              </div>
            </div>
            <div className="order-2">
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <SkeletonLoader type="custom" className="h-5 w-32 rounded" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <SkeletonLoader type="custom" className="h-5 w-5 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                      <SkeletonLoader type="custom" className="h-3 w-16 rounded" />
                    </div>
                  </div>
                ))}
                <SkeletonLoader type="button" className="w-full h-10 mt-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied UI
  if (accessDenied) {
    return (
      <div className="h-screen overflow-hidden flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-[5rem]">
            {/* Top colored bar */}
            <div className="h-1.5 bg-gradient-to-r from-red-500 to-red-500" />

            <div className="p-6">
              {/* Icon */}
              <div className="relative mx-auto w-16 h-16 mb-4">
                <div className="absolute inset-0 bg-red-100 rounded-full animate-pulse" />
                <div className="relative w-full h-full bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <Shield className="w-7 h-7 text-red-500" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
                Access Restricted
              </h1>

              {/* Description */}
              <p className="text-gray-500 text-center text-sm mb-5">
                You don't have permission to view this certification. It may not be assigned to you yet.
              </p>

              {/* Info Box */}
              <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-5">
                <div className="flex gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">
                    <span className="font-medium text-red-800">Need access?</span> Contact your administrator to request access.
                  </p>
                </div>
              </div>

              {/* Button */}
              <button
                onClick={() => navigate(isLearner ? '/learn/certifications' : '/admin/certifications')}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium text-sm shadow-md shadow-red-500/20"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Certifications
              </button>
            </div>
          </div>          
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Banner Skeleton */}
        <SkeletonLoader type="custom" className="w-full h-40 sm:h-56 md:h-80 lg:h-96 rounded-none" />

        {/* Title Section Skeleton */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8">
              <div className="flex-1 space-y-4">
                <SkeletonLoader type="custom" className="h-4 w-40 rounded" />
                <SkeletonLoader type="custom" className="h-8 w-3/4 rounded" />
                <div className="flex gap-4">
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-28 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                </div>
              </div>
              <SkeletonLoader type="button" className="h-12 w-36" />
            </div>
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex gap-6 py-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonLoader key={i} type="custom" className="h-5 w-20 rounded" />
              ))}
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="container mx-auto px-0 sm:px-4 py-4 sm:py-6 md:py-8">
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 order-1 min-w-0">
              <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-4">
                <SkeletonLoader type="title" />
                <SkeletonLoader type="text" count={4} />
                <SkeletonLoader type="custom" className="h-24 w-full rounded-lg mt-4" />
              </div>
            </div>
            {/* Sidebar */}
            <div className="order-2">
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <SkeletonLoader type="custom" className="h-5 w-32 rounded" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <SkeletonLoader type="custom" className="h-5 w-5 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                      <SkeletonLoader type="custom" className="h-3 w-16 rounded" />
                    </div>
                  </div>
                ))}
                <SkeletonLoader type="button" className="w-full h-10 mt-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!certificationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Certification not found</p>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner Image Section */}
      <div className="relative w-full h-40 sm:h-56 md:h-80 lg:h-96 bg-gradient-to-r from-brand-amber-600 to-brand-orange-600">
        {certificationData.banner_image_url || certificationData.thumbnail_image_url ? (
          <img
            src={certificationData.banner_image_url || certificationData.thumbnail_image_url}
            alt={certificationData.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Award className="w-24 h-24 md:w-32 md:h-32 text-white/30" />
          </div>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Back Button */}
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 bg-black/30 backdrop-blur-sm text-white rounded-lg hover:bg-black/50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Back to Certifications</span>
          </button>
        </div>
      </div>

      {/* Title and Info Section */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8">
            {/* Left: Title and Badge */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-5 h-5 md:w-6 md:h-6 text-brand-amber-600" />
                <span className="text-xs md:text-sm font-medium uppercase tracking-wider text-brand-amber-600">Professional Certification</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{certificationData.title}</h1>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 md:gap-6">
                <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600" />
                  <span>{certificationData.time_limit_minutes || 60} min</span>
                </div>
                <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                  <FileText className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600" />
                  <span>{getExamQuestionCount(certificationData)} questions</span>
                </div>
                <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                  <Target className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600" />
                  <span>{certificationData.passing_score}% to pass</span>
                </div>
                {certificationData.max_attempts && (
                  <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                    <Zap className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600" />
                    <span>{certificationData.max_attempts} attempts</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: CTA Button — learners only. The admin preview is read-only:
                taking the exam is not an admin action, and this navigates into the
                learner exam flow. */}
            {isLearner && (
            <div className="lg:flex-shrink-0">
              {(() => {
                const allAttemptsFinished = !!(certificationData.max_attempts &&
                                            attemptsUsed >= certificationData.max_attempts);
                const isDisabled = !canWrite || allAttemptsFinished;

                return (
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => canWrite && handleStartExam()}
                      disabled={isDisabled}
                      title={!canWrite ? 'You do not have permission to start exams' : undefined}
                      className={`w-full lg:w-auto px-6 md:px-8 py-3 md:py-4 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg text-sm md:text-base ${
                        isDisabled
                          ? 'bg-gray-400 cursor-not-allowed opacity-60'
                          : 'bg-brand-amber-600 hover:bg-brand-amber-700'
                      }`}
                    >
                      Start Exam
                      <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                    {allAttemptsFinished && (
                      <p className="text-xs md:text-sm text-red-600 font-medium text-center flex items-center justify-center gap-1">
                        <AlertCircle className="w-3 h-3 md:w-4 md:h-4" />
                        All attempts have been finished ({attemptsUsed})
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-[56px] lg:top-[64px] z-10 shadow-sm overflow-x-auto">
        <div className="container mx-auto px-4">
          <div className="flex gap-4 md:gap-8 min-w-max md:min-w-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-3 md:py-4 border-b-2 font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-brand-amber-600 text-brand-amber-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4 md:w-5 md:h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-0 sm:px-4 py-4 sm:py-6 md:py-8">
        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 order-1 min-w-0">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-brand-amber-600" />
                  About This Certification
                </h2>

                <div className="prose prose-sm md:prose-base max-w-none break-words">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {certificationData.description || 'This certification validates your expertise and professional knowledge in the subject area.'}
                  </ReactMarkdown>
                </div>

                <div className="mt-6 md:mt-8 p-4 md:p-6 bg-brand-amber-50 rounded-lg border border-brand-amber-200">
                  <h3 className="font-semibold text-brand-amber-900 mb-2 flex items-center gap-2 text-sm md:text-base">
                    <Shield className="w-4 h-4 md:w-5 md:h-5" />
                    What You'll Achieve
                  </h3>
                  <p className="text-brand-amber-800 text-sm md:text-base">
                    Upon successful completion, you'll earn a professional certification that validates your skills and knowledge.
                    This certification can boost your career prospects and demonstrate your expertise to employers.
                  </p>
                </div>
              </div>
            )}

            {/* Exam Info Tab */}
            {activeTab === 'exam-info' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-brand-amber-600" />
                  Exam Information
                </h2>

                <div className="space-y-6">
                  {/* Exam Structure */}
                  <div>
                    <h3 className="font-semibold text-base md:text-lg mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600" />
                      Exam Structure
                    </h3>
                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                      <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Total Questions</p>
                        <p className="text-xl md:text-2xl font-bold text-gray-900">{getExamQuestionCount(certificationData)}</p>
                      </div>
                      <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Time Limit</p>
                        <p className="text-xl md:text-2xl font-bold text-gray-900">{certificationData.time_limit_minutes || 60} min</p>
                      </div>
                      <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Passing Score</p>
                        <p className="text-xl md:text-2xl font-bold text-gray-900">{certificationData.passing_score}%</p>
                      </div>
                      <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Total Points</p>
                        <p className="text-xl md:text-2xl font-bold text-gray-900">{getExamTotalPointsLabel(certificationData)}</p>
                      </div>
                    </div>
                  </div>

                  <TierThresholds
                    tier_config={certificationData.tier_config}
                    passing_score={certificationData.passing_score}
                    variant="compact"
                  />

                  {/* Exam Settings */}
                  <div>
                    <h3 className="font-semibold text-base md:text-lg mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600" />
                      Exam Settings
                    </h3>
                    <div className="space-y-3">
                      {/* <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm md:text-base text-gray-700">Questions Shuffled</span>
                        <span className={`flex items-center gap-2 font-medium text-sm md:text-base ${certificationData.shuffle_questions ? 'text-green-600' : 'text-gray-400'}`}>
                          <CheckCircle className="w-4 h-4" />
                          {certificationData.shuffle_questions ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm md:text-base text-gray-700">Answer Options Shuffled</span>
                        <span className={`flex items-center gap-2 font-medium text-sm md:text-base ${certificationData.shuffle_options ? 'text-green-600' : 'text-gray-400'}`}>
                          <CheckCircle className="w-4 h-4" />
                          {certificationData.shuffle_options ? 'Yes' : 'No'}
                        </span>
                      </div> */}
                      {certificationData.max_attempts && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm md:text-base text-gray-700">Maximum Attempts</span>
                          <span className="font-medium text-sm md:text-base text-gray-900">{certificationData.max_attempts}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Requirements Tab */}
            {activeTab === 'requirements' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 md:w-6 md:h-6 text-brand-amber-600" />
                  Requirements & Tips
                </h2>

                <div className="space-y-6">
                  {/* Prerequisites */}
                  <div>
                    <h3 className="font-semibold text-base md:text-lg mb-3">Prerequisites</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm md:text-base">Complete understanding of the subject matter</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm md:text-base">Stable internet connection for the duration of the exam</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm md:text-base">Quiet environment free from distractions</span>
                      </li>
                    </ul>
                  </div>

                  {/* Exam Tips */}
                  <div className="p-4 md:p-6 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2 text-sm md:text-base">
                      <GraduationCap className="w-4 h-4 md:w-5 md:h-5" />
                      Tips for Success
                    </h3>
                    <ul className="space-y-2 text-blue-800">
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span className="text-sm md:text-base">Read each question carefully before answering</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span className="text-sm md:text-base">Manage your time wisely - you have {certificationData.time_limit_minutes || 60} minutes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span className="text-sm md:text-base">Review your answers before submitting</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span className="text-sm md:text-base">Stay calm and focused throughout the exam</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" />
                  Reviews
                </h2>
                <ReviewsList
                  targetId={id || ''}
                  targetType={ReviewType.CERTIFICATE}
                  targetTitle={certificationData?.title || ''}
                  showWriteReview={isLearner}
                  canWriteReview={canWrite}
                  currentUserId={user?.id}
                  isAdmin={!isLearner}
                  autoOpenReviewModal={shouldOpenReviewModal}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="order-2">
            {/* Exam Summary Card */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:sticky lg:top-24">
              <h3 className="font-semibold text-base md:text-lg mb-4">Exam Summary</h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm md:text-base text-gray-900">Duration</p>
                    <p className="text-xs md:text-sm text-gray-600">{certificationData.time_limit_minutes || 60} minutes</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm md:text-base text-gray-900">Questions</p>
                    <p className="text-xs md:text-sm text-gray-600">{getExamQuestionCount(certificationData)} questions</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Target className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm md:text-base text-gray-900">Passing Score</p>
                    <p className="text-xs md:text-sm text-gray-600">{certificationData.passing_score}% required</p>
                  </div>
                </div>

                {certificationData.max_attempts && (
                  <div className="flex items-start gap-3">
                    <Zap className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm md:text-base text-gray-900">Attempts</p>
                      <p className="text-xs md:text-sm text-gray-600">{certificationData.max_attempts} attempts allowed</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-brand-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm md:text-base text-gray-900">Last Updated</p>
                    <p className="text-xs md:text-sm text-gray-600">
                      {new Date(certificationData.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sidebar CTA — learners only, same reason as the header button. */}
              {isLearner && (
              <div className="mt-6 pt-6 border-t">
                {(() => {
                  const allAttemptsFinished = !!(
                    certificationData.max_attempts && attemptsUsed >= certificationData.max_attempts);
                  const isDisabled = !canWrite || allAttemptsFinished;

                  return (
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => canWrite && handleStartExam()}
                        disabled={isDisabled}
                        title={!canWrite ? 'You do not have permission to start exams' : undefined}
                        className={`w-full px-4 md:px-6 py-2 md:py-3 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all text-sm md:text-base ${
                          isDisabled
                            ? 'bg-gray-400 cursor-not-allowed opacity-60'
                            : 'bg-brand-amber-600 hover:bg-brand-amber-700'
                        }`}
                      >
                        Start Exam
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                      {allAttemptsFinished && (
                        <p className="text-xs md:text-sm text-red-600 font-medium text-center flex items-center justify-center gap-1">
                          <AlertCircle className="w-3 h-3 md:w-4 md:h-4" />
                          All attempts have been finished ({attemptsUsed})
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
